import React from "react";
import { Box, Container, Typography, Grid, Card } from "@mui/material";
import { DirectionsCar, Store, LocalOffer } from "@mui/icons-material";
import { motion } from "framer-motion";
import AnimatedSection from "../AnimatedSection";
import AnimatedCounter from "../AnimatedCounter";
import { gradients } from "../../../utils/theme";

/**
 * ProblemSection Component
 *
 * Displays the problem statement with animated statistics.
 * Shows the challenges customers face at malls.
 */
const ProblemSection: React.FC = () => {
  const stats = [
    {
      icon: DirectionsCar,
      value: 10000,
      suffix: "+",
      label: "Wait in parking queues daily",
      color: "#1a237e",
      delay: 0,
    },
    {
      icon: Store,
      value: 85,
      suffix: "%",
      label: "Can't locate stores quickly",
      color: "#FFD700",
      delay: 0.2,
    },
    {
      icon: LocalOffer,
      value: 70,
      suffix: "%",
      label: "Miss exclusive offers & events",
      color: "#1a237e",
      delay: 0.4,
    },
  ];

  return (
    <Box
      sx={{
        py: { xs: 4, sm: 5, md: 6, lg: 8 },
        background: "linear-gradient(180deg, #ffffff 0%, #f5f5f5 100%)",
      }}
    >
      <Container maxWidth="lg">
        <AnimatedSection>
          <Typography
            variant="h2"
            textAlign="center"
            sx={{
              fontSize: { xs: "2rem", md: "2.5rem" },
              fontWeight: 700,
              mb: 2,
              background: gradients.navyLux,
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
              backgroundClip: "text",
            }}
          >
            The Challenge
          </Typography>
          <Typography
            variant="h6"
            textAlign="center"
            color="text.secondary"
            sx={{ mb: 6, maxWidth: "800px", mx: "auto" }}
          >
            Endless parking queues. Hidden stores. Missed offers & events. Mall
            visits shouldn't be this hard.
          </Typography>
        </AnimatedSection>

        <Grid container spacing={3}>
          {stats.map((stat, index) => (
            <Grid item xs={12} sm={6} md={4} key={index}>
              <AnimatedSection delay={stat.delay}>
                <motion.div
                  whileHover={{ scale: 1.05 }}
                  transition={{ type: "spring", stiffness: 300 }}
                >
                  <Card
                    elevation={0}
                    sx={{
                      textAlign: "center",
                      p: 3,
                      height: "100%",
                      border: "1px solid",
                      borderColor: "divider",
                      transition: "all 0.3s ease",
                      "&:hover": {
                        boxShadow: 3,
                      },
                    }}
                  >
                    <stat.icon
                      sx={{ fontSize: 64, color: stat.color, mb: 2 }}
                    />
                    <Typography variant="h4" sx={{ mb: 1, fontWeight: 700 }}>
                      <AnimatedCounter end={stat.value} suffix={stat.suffix} />
                    </Typography>
                    <Typography variant="body1" color="text.secondary">
                      {stat.label}
                    </Typography>
                  </Card>
                </motion.div>
              </AnimatedSection>
            </Grid>
          ))}
        </Grid>
      </Container>
    </Box>
  );
};

export default ProblemSection;
